/**
 * Procurement Demo — Nami scripted prompts.
 *
 * THROWAWAY DEMO CODE. Mirrors the AP demo's deterministic chat spine
 * (`@/demo/ap/agent/script`): a few prompts map to (a) streamed narration and
 * (b) a page to open once the narration finishes. The chat interceptor matches a
 * user message, bypasses the real RPC, streams the narration, then navigates.
 *
 * Numbers are the canonical ones from the procurement demo README; supplier
 * names are rendered from `data/suppliers`. `script.test.ts` cross-checks the
 * figures that live in shared data. Citations are never quoted here: the pages
 * the user lands on own the agreement text.
 */

import { getSupplier } from '../data/suppliers';
import type { ContractId, MaterialId, SupplierId } from '../data/types';

/** Where a scripted turn lands. Each variant is a typed router target. */
export type ProcurementNavTarget =
  | { to: '/procurement/invoice-compliance'; search: { invoice: string } }
  | {
      to: '/procurement/contracts/$contractId';
      params: { contractId: ContractId };
      search: { clause: string };
    }
  | {
      to: '/procurement/materials/$materialId';
      params: { materialId: MaterialId };
    }
  | { to: '/procurement/forecast/bom' }
  | { to: '/procurement/forecast/details'; search: { series: string } };

export type ProcurementDemoScriptId =
  | 'invoice-holds'
  | 'gently-sla'
  | 'thhn-best-supplier'
  | 'vitrex-hold'
  | 'vfd-forecast';

export interface ProcurementDemoScript {
  id: ProcurementDemoScriptId;
  /**
   * Phrasings that match on their own. A message matches a prompt when it
   * equals it or contains every word of it (whole-word, plural-insensitive).
   * Multi-word aliases only, and each one carries a distinctive domain word.
   */
  prompts: string[];
  /**
   * Looser fallback: synonym groups (AND-of-ORs). A message matches when every
   * group has at least one whole-word hit; most hits wins.
   */
  intent: string[][];
  /** Nami's streamed reply. */
  narration: string;
  /** Page opened after the narration. */
  target: ProcurementNavTarget;
}

function name(id: SupplierId): string {
  return getSupplier(id).shortName;
}

const CORVANE = name('sup-corvane');
const HOLLIS = name('sup-hollis');
const KAISEI = name('sup-kaisei');
const VITREX = name('sup-vitrex');
const LUMEN = name('sup-lumen');
const HALBROOK = name('sup-halbrook');
const TRANSCON = name('sup-transcon');
const GENTLY = name('sup-gently');

export const GENTLY_MSA_ID: ContractId = 'MSA-GEN-2025-001';
export const GENTLY_SERVICE_CREDIT_CLAUSE = `${GENTLY_MSA_ID}:B.3`;
export const CORVANE_HOLD_INVOICE_ID = 'CV-2026-08812';
export const VFD_PANEL_SHOP_SERIES = 'VFD-50HP-480@PNL';

export const PROCUREMENT_DEMO_SCRIPTS: ProcurementDemoScript[] = [
  {
    id: 'invoice-holds',
    prompts: [
      'which invoices are on hold for contract violations',
      'invoices on hold for contract violations',
      'which invoices are on hold',
      'invoices on hold',
      'held invoices',
      'invoice contract violations',
      'contract violations',
      'which invoices are on hold for not following the agreement',
      'invoices that break the agreement',
      'invoices that do not match the agreement',
      'overbilled invoices',
    ],
    intent: [
      ['invoice', 'bill'],
      [
        'hold',
        'held',
        'violation',
        'noncompliant',
        'overbilled',
        'overcharge',
        'leakage',
        'flag',
      ],
      ['contract', 'agreement', 'violation', 'compliance', 'noncompliant'],
    ],
    narration: `Four invoices are on hold for contract violations, with $113,578 recoverable in total. ${CORVANE} ${CORVANE_HOLD_INVOICE_ID} is flagged three ways: it bills Net 30 against the Net 45 in the agreement, charges $431.50 per 100 ft against the $424.36 maximum price, and adds $3,850 of freight although freight is included in the price. The other three are ${LUMEN} with a tariff surcharge although it pays duties and tariffs, ${HALBROOK} over its budget cap and billing a role that is not among its agreed hourly rates, and ${TRANSCON} with fuel over the cap. I've opened the ${CORVANE} invoice with each failed check and the clause behind it.`,
    target: {
      to: '/procurement/invoice-compliance',
      search: { invoice: CORVANE_HOLD_INVOICE_ID },
    },
  },
  {
    id: 'gently-sla',
    prompts: [
      'what are the sla terms with gently ai',
      'sla terms with gently ai',
      'gently ai sla terms',
      'gently ai sla',
      'gently sla terms',
      'gently sla',
      'gently uptime commitment',
      'what service levels does gently ai commit to',
      'service levels with gently ai',
      'gently ai service levels',
      'gently service levels',
      'gently uptime promise',
    ],
    intent: [
      ['gently'],
      [
        'sla',
        'uptime',
        'credit',
        'rpo',
        'rto',
        'level',
        'availability',
        'downtime',
      ],
    ],
    narration: `Under the ${GENTLY} master agreement (${GENTLY_MSA_ID}), Exhibit B sets 99.9% monthly uptime, excluding up to 4 hours of maintenance announced 72 hours ahead. Support response is 1 hour at any time for urgent issues, 4 business hours for high-priority ones and 1 business day for normal ones. Missed uptime earns fee credits of 5%, 10% or 25% of the monthly fee, capped at 25%, repeated failures give us a right to exit, and the recovery targets are at most 1 hour of data lost and service back within 4 hours. I've opened Exhibit B with the fee credit clause highlighted.`,
    target: {
      to: '/procurement/contracts/$contractId',
      params: { contractId: GENTLY_MSA_ID },
      search: { clause: GENTLY_SERVICE_CREDIT_CLAUSE },
    },
  },
  {
    id: 'thhn-best-supplier',
    prompts: [
      'who is our best supplier for 1 0 thhn wire',
      'best supplier for 1 0 thhn wire',
      'best supplier for thhn wire',
      'best supplier for thhn',
      'best thhn supplier',
      'compare thhn suppliers',
      'thhn supplier comparison',
      'thhn wire suppliers',
      'who is our best supplier for copper building wire',
      'best supplier for copper building wire',
      'best supplier for copper wire',
      'compare copper wire suppliers',
      'copper building wire suppliers',
      'copper wire supplier comparison',
    ],
    intent: [
      ['thhn', 'wire', 'copper'],
      [
        'supplier',
        'vendor',
        'tco',
        'compare',
        'comparison',
        'best',
        'cheapest',
        'source',
      ],
    ],
    narration: `${CORVANE} has the lowest unit price for 1/0 copper building wire at $424.36 per 100 ft, but ${HOLLIS} has the lowest total cost at $440.20 per 100 ft against $446.10 for ${CORVANE} and $468.90 for ${KAISEI}. ${CORVANE}'s 4 quality issues cost $1.1M in returns, re-spooling and customer credits, plus 31 hours of wire-cutting downtime, while ${HOLLIS} delivers on time and in full 98.1% of the time against 91.0%. I recommend shifting 15 percentage points of volume from ${CORVANE} to ${HOLLIS} at the December 31 renewal, which is worth about $0.9M a year, and I've opened the supplier comparison.`,
    target: {
      to: '/procurement/materials/$materialId',
      params: { materialId: 'MAT-THHN' },
    },
  },
  {
    id: 'vitrex-hold',
    prompts: [
      'what is at risk from the vitrex breaker hold',
      'at risk from the vitrex hold',
      'vitrex breaker hold',
      'vitrex hold',
      'vitrex breaker impact',
      'vitrex quality hold',
      'what is at risk from the vitrex circuit breaker hold',
      'vitrex circuit breaker hold',
      'vitrex circuit breaker impact',
    ],
    intent: [
      ['vitrex'],
      [
        'risk',
        'hold',
        'breaker',
        'mccb',
        'panel',
        'impact',
        'affect',
        'quarantine',
        'escape',
        'lot',
        'exposure',
      ],
    ],
    narration: `${VITREX} lot VX-24117 of 800A breakers shipped from a factory UL has not certified, which breaches Quality Agreement section 7.2, so the lot is quarantined at the control panel shop. With the hold in place, 34 motor control and power distribution panel builds are at risk over the next 8 weeks. The fix is to swap in ${HOLLIS}'s qualified backup breaker, MCCB-800A-H, at $4,020 each, which is $170 more than ${VITREX}, for about 6 weeks. I've opened the bill of materials with the affected panels.`,
    target: { to: '/procurement/forecast/bom' },
  },
  {
    id: 'vfd-forecast',
    prompts: [
      'show me the forecast for vfds at the panel shop',
      'forecast for vfds at the panel shop',
      'show me the vfd forecast',
      'forecast for vfds',
      'vfd forecast',
      'vfd demand forecast',
      'panel shop vfd forecast',
      'show me the forecast for drives at the panel shop',
      'show me the forecast for drives at the control panel shop',
      'forecast for drives at the panel shop',
      'forecast for drives',
      'forecast for motor drives',
      'motor drive forecast',
      'drive forecast',
      'control panel shop drive forecast',
    ],
    intent: [
      ['vfd', 'drive', 'motor'],
      ['forecast', 'demand', 'projection', 'outlook'],
    ],
    narration: `Here is the forecast for the motor drive (50 HP, 480 V, VFD-50HP-480) at the control panel shop: demand follows the build plan, with six drives going into each motor control panel and one into each drive retrofit kit, and it climbs over the next six months as that plan grows. ${LUMEN} is still the only supplier on a 12-week lead time, so I've opened this forecast on the forecast details page with its demand history, method and overrides.`,
    target: {
      to: '/procurement/forecast/details',
      search: { series: VFD_PANEL_SHOP_SERIES },
    },
  },
];

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Plural-insensitive: "invoices" and "invoice", "vfds" and "vfd" compare equal. */
function stem(token: string): string {
  return token.length > 3 && token.endsWith('s') && !token.endsWith('ss')
    ? token.slice(0, -1)
    : token;
}

function tokenize(text: string): string[] {
  const norm = normalize(text);
  if (!norm) return [];
  return norm.split(' ').map(stem);
}

function scoreIntent(tokens: Set<string>, groups: string[][]): number | null {
  let total = 0;
  for (const group of groups) {
    const hits = group.filter((syn) => tokens.has(stem(syn))).length;
    if (hits === 0) return null;
    total += hits;
  }
  return total;
}

/** Find the scripted beat for a user message, or null if none matches. */
export function matchProcurementDemoScript(
  message: string,
): ProcurementDemoScript | null {
  const messageWords = tokenize(message);
  if (messageWords.length === 0) return null;
  const tokens = new Set(messageWords);

  // (1) Precise fast path: the message equals a prompt or contains all of its
  // words.
  for (const script of PROCUREMENT_DEMO_SCRIPTS) {
    for (const prompt of script.prompts) {
      const promptTokens = tokenize(prompt);
      if (promptTokens.length < 2) continue;
      if (promptTokens.every((token) => tokens.has(token))) return script;
    }
  }

  // (2) Intent fallback: best-scoring beat whose every group has a hit.
  let best: ProcurementDemoScript | null = null;
  let bestScore = 0;
  for (const script of PROCUREMENT_DEMO_SCRIPTS) {
    const score = scoreIntent(tokens, script.intent);
    if (score !== null && score > bestScore) {
      best = script;
      bestScore = score;
    }
  }
  return best;
}
